import React, { useState, useMemo, useEffect } from 'react'
import { Tabs, Tab, Modal, Spinner, Pagination, Button } from 'react-bootstrap'
import { Grid, X, ChevronLeft, ChevronRight, Tag } from 'lucide-react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useFetchAnnotatedContentQuery } from '../../../../services/contentApi'
import { Project } from '../../../../types/models/project'
import { Content } from '../../../../types/models/content'
import { useAppDispatch } from '../../../../store/hooks'
import { useDatasetStats } from '../../../../utils/datasetStats'
import {
  setSelectedContentId,
  setEditingContent,
  clearAllBoundingBoxes,
} from '../../../../store/slices/annotationSlice'
import { setDatasetSources } from '../../../../store/slices/datasetSlice'
import { setActiveLabelingSubView } from '../../../../store/slices/projectSlice'
import GalleryScaffold from '../../../gallery/Scaffold'
import GalleryGrid from '../../../gallery/Grid'
import GalleryTile from '../../../gallery/Tile'
import ContentImage from '../../../tools/ContentImage'

const ITEMS_PER_PAGE = 200

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  padding: 0.5rem 1rem;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(102, 126, 234, 0.4);
    box-shadow: 0 25px 50px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
  }

  .pagination {
    margin: 0;
  }

  .page-link {
    background-color: transparent;
    border-color: rgba(102, 126, 234, 0.3);
    color: #fff;
    padding: 0.35rem 0.6rem;
    transition: all 0.3s ease;
    border-radius: 8px;
    margin: 0 2px;

    &:hover {
      background-color: rgba(102, 126, 234, 0.2);
      border-color: rgba(102, 126, 234, 0.5);
      transform: translateY(-1px);
    }
  }

  .page-item.active .page-link {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
    color: #fff;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
  }

  .page-item.disabled .page-link {
    background-color: transparent;
    border-color: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.3);
  }
`

const PaginationInfo = styled.div`
  color: #fff;
  font-size: 0.85rem;
  white-space: nowrap;
  padding: 0.35rem 0.75rem;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  font-weight: 500;
`

const ImagesContainer = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: transparent;
  padding: 1.25rem;
  gap: 1.25rem;
  overflow: hidden;

  .images-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
    gap: 1rem;

    .header-info {
      flex: 1;
      h5 {
        color: #ffffff;
        margin: 0;
        font-weight: 600;
      }
    }

    .header-center {
      display: flex;
      justify-content: center;
      align-items: center;
      flex: 1;
    }

    .view-controls {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      flex: 1;
      justify-content: flex-end;

      .btn {
        background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
        border: 1px solid rgba(102, 126, 234, 0.2);
        color: #b0b0b0;
        padding: 0.5rem 0.75rem;
        border-radius: 8px;
        transition: all 0.3s ease;
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        backdrop-filter: blur(10px);

        &.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-color: #667eea;
          color: white;
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
          transform: translateY(-1px);
        }

        &:hover:not(.active) {
          background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
          border-color: rgba(102, 126, 234, 0.4);
          color: #ffffff;
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
        }
      }
    }
  }

  .gallery-container {
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }

  .scroll-container {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;

    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgba(102, 126, 234, 0.3);
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: rgba(102, 126, 234, 0.5);
    }
  }
`

const ImageModal = styled(Modal)`
  .modal-content {
    background: #1a1a1a;
    border: none;
    border-radius: 16px;
    overflow: hidden;
  }

  .modal-body {
    padding: 0;
    position: relative;
    background: #1a1a1a;
  }
`

const ImageModalContainer = styled.div`
  position: relative;
  width: 100%;
  height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
`

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  background: rgba(26, 26, 26, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(26, 26, 26, 0.95);
    border-color: rgba(255, 255, 255, 0.4);
  }
`

const LabelButton = styled.button`
  position: absolute;
  top: 1rem;
  left: 1rem;
  z-index: 10;
  background: #0d6efd;
  border: none;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;

  &:hover {
    background: #0b5ed7;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.4);
  }

  &:active {
    transform: translateY(0);
  }
`

const NavigationButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  background: rgba(26, 26, 26, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(26, 26, 26, 0.95);
    border-color: rgba(255, 255, 255, 0.4);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  &.left {
    left: 1rem;
  }

  &.right {
    right: 1rem;
  }
`

interface DatasetImagesTabProps {
  datasetSource: string
  projectId: string
  project?: Project
}

const DatasetImagesTab: React.FC<DatasetImagesTabProps> = ({
  datasetSource,
  projectId,
  project,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [imagesPerRow, setImagesPerRow] = useState(6)
  const [activeTab, setActiveTab] = useState<'annotated' | 'unannotated'>('annotated')
  const [showImageModal, setShowImageModal] = useState(false)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [annotatedPage, setAnnotatedPage] = useState(1)
  const [unannotatedPage, setUnannotatedPage] = useState(1)

  const handleLabelClick = () => {
    // Set the dataset source in Redux state
    dispatch(setDatasetSources([datasetSource]))
    // Set the active labeling subview to 'Labeling' to select the correct tab
    dispatch(setActiveLabelingSubView('Labeling'))
    // Navigate to unified labeling view (this keeps the sidebar in sync)
    navigate(`/labeling/${projectId}`)
  }

  // Get stats for this datasource
  const datasetStats = useDatasetStats(projectId, project?.datasetid || '', [datasetSource])
  const currentStats = datasetStats[datasetSource]

  // Fetch both annotated and unannotated content in parallel (prefetching)
  // This allows data to be cached before the user switches tabs
  const {
    data: annotatedData,
    isLoading: isAnnotatedLoading,
    error: annotatedError,
  } = useFetchAnnotatedContentQuery(
    {
      project_id: projectId,
      dataset_id: project?.datasetid || '',
      page: annotatedPage - 1, // Convert 1-based to 0-based
      limit: ITEMS_PER_PAGE,
      metadata_filter: { datasource: [datasetSource] },
      sort_key: 'updated_at',
      sort_val: -1,
    },
    { skip: !projectId || !datasetSource }
  )

  // Fetch unannotated content (all images, will filter client-side)
  const {
    data: unannotatedData,
    isLoading: isUnannotatedLoading,
    error: unannotatedError,
  } = useFetchAnnotatedContentQuery(
    {
      project_id: projectId,
      dataset_id: undefined,
      page: unannotatedPage - 1, // Convert 1-based to 0-based
      limit: ITEMS_PER_PAGE,
      metadata_filter: { datasource: [datasetSource] },
      sort_key: 'updated_at',
      sort_val: -1,
    },
    { skip: !projectId || !datasetSource }
  )

  // Get appropriate data based on active tab
  const currentData = activeTab === 'annotated' ? annotatedData : unannotatedData
  const isLoading = activeTab === 'annotated' ? isAnnotatedLoading : isUnannotatedLoading
  const error = activeTab === 'annotated' ? annotatedError : unannotatedError

  // Filter images for unannotated tab
  const displayImages = useMemo(() => {
    const images = currentData?.content || []
    if (activeTab === 'annotated') {
      return images
    } else {
      // Filter out images that have annotations
      return images.filter(
        (img) => !img.annotation || !Array.isArray(img.annotation) || img.annotation.length === 0
      )
    }
  }, [currentData?.content, activeTab])

  // Use stats for counts
  const annotatedCount = currentStats?.total_annotated_images || 0
  const unannotatedCount = currentStats?.total_unannotated_images || 0
  const totalCount = activeTab === 'annotated' ? annotatedCount : unannotatedCount

  // Pagination calculations
  const currentPage = activeTab === 'annotated' ? annotatedPage : unannotatedPage
  const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE))

  // Show range based on how many images are actually visible on this page (displayImages),
  // not just the theoretical max per page, so the count matches what the user sees.
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1
  const endItem = totalCount === 0 ? 0 : startItem + Math.max(displayImages.length - 1, 0)

  const handlePageChange = (page: number) => {
    if (activeTab === 'annotated') {
      setAnnotatedPage(page)
    } else {
      setUnannotatedPage(page)
    }
  }

  // Reset to page 1 when switching datasource
  useEffect(() => {
    setAnnotatedPage(1)
    setUnannotatedPage(1)
  }, [datasetSource])

  const handleImageClick = (content: Content, index: number) => {
    setSelectedImageIndex(index)
    setShowImageModal(true)
    dispatch(setSelectedContentId(content.id))
    dispatch(setEditingContent(content))
    dispatch(clearAllBoundingBoxes())
  }

  const handlePrevImage = () => {
    if (selectedImageIndex > 0) {
      setSelectedImageIndex(selectedImageIndex - 1)
    }
  }

  const handleNextImage = () => {
    if (selectedImageIndex < displayImages.length - 1) {
      setSelectedImageIndex(selectedImageIndex + 1)
    }
  }

  const handleLabelFromModal = () => {
    const currentContent = displayImages[selectedImageIndex]
    if (currentContent) {
      // Ensure the content is set for editing
      dispatch(setEditingContent(currentContent))
      dispatch(setSelectedContentId(currentContent.id))
      // Set the dataset source in Redux state
      dispatch(setDatasetSources([datasetSource]))
      // Set the active labeling subview to 'Labeling' to select the correct tab
      dispatch(setActiveLabelingSubView('Labeling'))
      // Navigate to unified labeling view
      navigate(`/labeling/${projectId}`)
    }
  }

  if (error) {
    return (
      <ImagesContainer>
        <div className="d-flex justify-content-center align-items-center h-100">
          <div className="text-center">
            <h3 className="text-white">Error Loading Images</h3>
            <p className="text-muted">There was an error loading the images for this dataset.</p>
          </div>
        </div>
      </ImagesContainer>
    )
  }

  return (
    <ImagesContainer>
      <div className="images-header">
        <div className="header-info">
          <h5>{activeTab === 'annotated' ? 'Annotated Images' : 'Unannotated Images'}</h5>
        </div>
        <div className="header-center">
          <Button
            variant="primary"
            onClick={handleLabelClick}
            className="d-flex align-items-center gap-2"
          >
            <Tag size={16} />
            Label
          </Button>
        </div>
        <div className="view-controls">
          <PaginationContainer>
            <Pagination>
              <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              <Pagination.Item active>{currentPage}</Pagination.Item>
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
              <Pagination.Last
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
            <PaginationInfo>
              {startItem}-{endItem} of {totalCount} images
            </PaginationInfo>
          </PaginationContainer>
          <button
            className={`btn ${imagesPerRow === 3 ? 'active' : ''}`}
            onClick={() => setImagesPerRow(3)}
            title="3 per row"
          >
            <Grid size={16} />
          </button>
          <button
            className={`btn ${imagesPerRow === 6 ? 'active' : ''}`}
            onClick={() => setImagesPerRow(6)}
            title="6 per row"
          >
            <Grid size={16} />
          </button>
          <button
            className={`btn ${imagesPerRow === 9 ? 'active' : ''}`}
            onClick={() => setImagesPerRow(9)}
            title="9 per row"
          >
            <Grid size={16} />
          </button>
        </div>
      </div>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k as 'annotated' | 'unannotated')}>
        <Tab eventKey="annotated" title={`Annotated (${annotatedCount})`}>
          <div className="gallery-container">
            <div className="scroll-container">
              <GalleryScaffold
                isLoading={isLoading}
                empty={!displayImages.length && !isLoading}
                emptyMessage="No annotated images found in this dataset."
              >
                <GalleryGrid imagesPerRow={imagesPerRow}>
                  {displayImages.map((image: Content, index: number) => (
                    <GalleryTile key={image.id} onClick={() => handleImageClick(image, index)}>
                      <ContentImage
                        content={image}
                        onImageClick={() => handleImageClick(image, index)}
                        selectable={false}
                      />
                    </GalleryTile>
                  ))}
                </GalleryGrid>
              </GalleryScaffold>
            </div>
          </div>
        </Tab>

        <Tab eventKey="unannotated" title={`Unannotated (${unannotatedCount})`}>
          <div className="gallery-container">
            <div className="scroll-container">
              <GalleryScaffold
                isLoading={isLoading}
                empty={!displayImages.length && !isLoading}
                emptyMessage="No unannotated images found in this dataset."
              >
                <GalleryGrid imagesPerRow={imagesPerRow}>
                  {displayImages.map((image: Content, index: number) => (
                    <GalleryTile key={image.id} onClick={() => handleImageClick(image, index)}>
                      <ContentImage
                        content={image}
                        onImageClick={() => handleImageClick(image, index)}
                        selectable={false}
                      />
                    </GalleryTile>
                  ))}
                </GalleryGrid>
              </GalleryScaffold>
            </div>
          </div>
        </Tab>
      </Tabs>

      {/* Image Preview Modal */}
      <ImageModal show={showImageModal} onHide={() => setShowImageModal(false)} centered size="xl">
        <Modal.Body style={{ padding: 0 }}>
          <ImageModalContainer>
            <CloseButton onClick={() => setShowImageModal(false)}>
              <X size={20} />
            </CloseButton>

            <LabelButton onClick={handleLabelFromModal}>
              <Tag size={16} />
              Label
            </LabelButton>

            <NavigationButton
              className="left"
              onClick={handlePrevImage}
              disabled={selectedImageIndex === 0}
            >
              <ChevronLeft size={24} />
            </NavigationButton>

            <NavigationButton
              className="right"
              onClick={handleNextImage}
              disabled={selectedImageIndex === displayImages.length - 1}
            >
              <ChevronRight size={24} />
            </NavigationButton>

            {displayImages[selectedImageIndex] ? (
              <div style={{ width: '100%', height: '100%', pointerEvents: 'none' }}>
                <ContentImage
                  content={displayImages[selectedImageIndex]}
                  onImageClick={() => undefined}
                  selectable={false}
                />
              </div>
            ) : (
              <Spinner animation="border" role="status" style={{ color: 'white' }}>
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            )}
          </ImageModalContainer>
        </Modal.Body>
      </ImageModal>
    </ImagesContainer>
  )
}

export default DatasetImagesTab
